/**
 * Vortex Command Centre — Skipper/Navigator persona scenarios.
 *
 * 8 top-scoring scenarios from Pupil-inspired evaluation (avg 0.916).
 * Each represents a real sailing workflow backed by actual Vortex capabilities.
 *
 * Persona: Skipper/Navigator — ocean racing, passage planning, tactical decisions
 * Core thesis: Model disagreement IS the uncertainty signal. Confidence is earned, not asserted.
 */

export interface OutputLine {
  text: string;
  type: 'header' | 'info' | 'success' | 'warning' | 'error' | 'dimmed' | 'accent' | 'data';
}

export interface Highlight {
  label: string;
  value: string;
  color: 'cyan' | 'green' | 'yellow' | 'red';
}

export interface Scenario {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  command?: string;
  outputLines: OutputLine[];
  highlight?: Highlight;
}

export const SCENARIOS: Scenario[] = [
  // ── 1. Model Disagreement Warning (score: 0.950) ──────
  // Core thesis: When models split, the uncertainty IS the signal
  {
    id: 'disagreement',
    title: 'Model Disagreement',
    subtitle: 'When models split — the uncertainty IS the signal',
    category: 'Tactical',
    command: 'vortex agreement --hours 6',
    outputLines: [
      { text: '⚠ MODEL DISAGREEMENT — wind speed', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  HRRR:  28kt  ████████████████████████████', type: 'error' },
      { text: '  GFS:   18kt  ██████████████████', type: 'info' },
      { text: '  ECMWF: 22kt  ██████████████████████', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  Spread: 10kt — significant disagreement', type: 'warning' },
      { text: '  Interpretation: Reef for 28, hope for 18', type: 'accent' },
      { text: '  Next update: 18Z HRRR verifies first (+4hr)', type: 'dimmed' },
    ],
    highlight: { label: 'Key Insight', value: "Don't average — reef early when models split", color: 'yellow' },
  },

  // ── 2. Ensemble Dashboard (score: 0.930) ──────────────
  // Shows real-time field-level model competition
  {
    id: 'ensemble',
    title: 'Ensemble Dashboard',
    subtitle: 'Real-time model competition — who\'s winning right now',
    category: 'Tactical',
    command: 'vortex models --station 41002 --fields wind_speed,wind_dir',
    outputLines: [
      { text: '═══ Field-Level Competition ═══', type: 'header' },
      { text: '', type: 'dimmed' },
      { text: '  wind_speed  →  HRRR winning', type: 'success' },
      { text: '    MAE: 1.8kt vs GFS 3.1kt vs ECMWF 2.9kt', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  wind_dir    →  ECMWF winning', type: 'success' },
      { text: '    MAE: 8° vs HRRR 14° vs GFS 11°', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  Ensemble: HRRR speed + ECMWF direction', type: 'accent' },
      { text: '  Combined MAE: 2.59kt (better than any single model)', type: 'info' },
    ],
    highlight: { label: 'Ensemble Advantage', value: 'Best-of-each-field, not averaging', color: 'cyan' },
  },

  // ── 3. Calibration Proof (score: 0.917) ───────────────
  // Earned confidence — validated against observations
  {
    id: 'calibration',
    title: 'Calibration Proof',
    subtitle: 'When we say 80% confidence, we mean it — validated',
    category: 'Trust',
    command: 'vortex calibration --period 90d',
    outputLines: [
      { text: '═══ Reliability Diagram (90-day) ═══', type: 'header' },
      { text: '', type: 'dimmed' },
      { text: '  Predicted 90%  →  Observed 88.7%', type: 'success' },
      { text: '  Predicted 80%  →  Observed 78.2%', type: 'success' },
      { text: '  Predicted 70%  →  Observed 71.4%', type: 'success' },
      { text: '  Predicted 60%  →  Observed 63.1%', type: 'success' },
      { text: '', type: 'dimmed' },
      { text: '  EMOS calibration: 35% CRPS improvement', type: 'info' },
      { text: '  Validated against 38,992 HRRR observation pairs', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  Confidence is earned through validation, not asserted.', type: 'accent' },
    ],
    highlight: { label: 'EMOS Calibration', value: '35% improvement — ranges, not points', color: 'green' },
  },

  // ── 4. Nowcast Alert (score: 0.915) ───────────────────
  // Proactive tactical warning from 6-model blend
  {
    id: 'nowcast',
    title: 'Nowcast Alert',
    subtitle: 'Short-range wind shift detected — 2 hour warning',
    category: 'Tactical',
    command: 'vortex nowcast --station 41002 --alert',
    outputLines: [
      { text: '⚠ WIND SHIFT ALERT — 2hr lead time', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  Current:  SW 14kt, TWA 45°', type: 'info' },
      { text: '  Nowcast:  WSW→W 20kt by 1400Z', type: 'warning' },
      { text: '  Source:   Winfield 6-model blend', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  Confidence: 85% (4/6 models agree)', type: 'success' },
      { text: '  XGBoost: SST gradient detected at boundary', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  → Prepare for header, consider early port tack', type: 'accent' },
    ],
    highlight: { label: 'Nowcast Accuracy', value: '+22% vs single models (6-model blend)', color: 'cyan' },
  },

  // ── 5. Observation vs Forecast Divergence (score: 0.913) ──
  // Reality disagrees with all models — trust your instruments
  {
    id: 'divergence',
    title: 'Forecast Divergence',
    subtitle: 'Reality is diverging from all models — pay attention',
    category: 'Proactive',
    command: 'vortex verify --realtime',
    outputLines: [
      { text: '⚠ FORECAST DIVERGENCE DETECTED', type: 'error' },
      { text: '', type: 'dimmed' },
      { text: '  Observed:  NW 22kt (NDBC 41002, live)', type: 'warning' },
      { text: '  HRRR:      SW 15kt ← wrong direction', type: 'dimmed' },
      { text: '  GFS:       SW 14kt ← wrong direction', type: 'dimmed' },
      { text: '  ECMWF:     W  17kt ← partial miss', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  Divergence: 45° direction, 7kt speed', type: 'error' },
      { text: '  Interpretation: Models missed local effect', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  → Trust your instruments until next model update', type: 'accent' },
    ],
    highlight: { label: 'Ground Truth', value: 'NDBC buoys override stale models', color: 'red' },
  },

  // ── 6. Departure Window (score: 0.910) ────────────────
  // Multi-model consensus for passage timing
  {
    id: 'departure',
    title: 'Departure Window',
    subtitle: 'Optimal departure timing from multi-model consensus',
    category: 'Pre-Departure',
    command: 'vortex departure --from 41.38,-71.51 --to 41.52,-70.67 --window 72h',
    outputLines: [
      { text: '═══ Departure Analysis ═══', type: 'header' },
      { text: '', type: 'dimmed' },
      { text: '  ✓ Best window: Thu 0600Z', type: 'success' },
      { text: '    Model agreement: 92% (all 3 within 3kt)', type: 'success' },
      { text: '    Conditions: SW 12-15kt, seas 1.5m, fair', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  ✗ Avoid: Fri 1200-1800Z', type: 'error' },
      { text: '    25-30kt SW building, HRRR/GFS split on timing', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  Confidence: HIGH — 3 models converge', type: 'accent' },
    ],
    highlight: { label: 'Window', value: 'Thu 0600Z — 92% model agreement', color: 'green' },
  },

  // ── 7. Confidence Degradation (score: 0.900) ─────────
  // Forecast quality dropping — know when to hedge
  {
    id: 'confidence-trend',
    title: 'Confidence Trend',
    subtitle: 'Forecast quality dropping — hedge your bets',
    category: 'Heavy Weather',
    command: 'vortex confidence --trend',
    outputLines: [
      { text: '⚠ CONFIDENCE DEGRADING', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  6hr ago:  91% ensemble confidence', type: 'success' },
      { text: '  3hr ago:  78% confidence', type: 'info' },
      { text: '  Now:      67% confidence ↓', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  Cause: HRRR/ECMWF diverging on frontal timing', type: 'info' },
      { text: '  Next reliable update: 18Z HRRR run (+3hr)', type: 'dimmed' },
      { text: '', type: 'dimmed' },
      { text: '  → Hold conservative sail plan until recovery', type: 'accent' },
    ],
    highlight: { label: 'Advice', value: 'When confidence drops, reef — don\'t guess', color: 'yellow' },
  },

  // ── 8. Forecast Accuracy Report (score: 0.900) ────────
  // Post-race validated accuracy — trust through transparency
  {
    id: 'accuracy',
    title: 'Forecast Accuracy',
    subtitle: 'How accurate were we? Validated against buoy observations.',
    category: 'Post-Race',
    command: 'vortex accuracy --race edgartown_2025 --vs ndbc',
    outputLines: [
      { text: '═══ Accuracy Report ═══', type: 'header' },
      { text: '', type: 'dimmed' },
      { text: '  Overall:    2.3kt MAE (wind speed)', type: 'info' },
      { text: '              12° MAE (wind direction)', type: 'info' },
      { text: '', type: 'dimmed' },
      { text: '  ✓ Best:  0-6hr nowcast — 1.4kt MAE', type: 'success' },
      { text: '  ~ Fair:  6-24hr forecast — 2.8kt MAE', type: 'info' },
      { text: '  ✗ Worst: 36-48hr — 4.1kt MAE (front 6hr late)', type: 'warning' },
      { text: '', type: 'dimmed' },
      { text: '  Validated against 9 NDBC stations, 2,457 pairs', type: 'dimmed' },
      { text: '  vs PredictWind: 3.8kt MAE over same period', type: 'accent' },
    ],
    highlight: { label: 'Tactical Edge', value: '0-6hr nowcast: 1.4kt MAE — trust this range', color: 'green' },
  },
];

export const CYCLE_DURATION_MS = 9000;
